import { forwardRef } from "react";
import type { ButtonHTMLAttributes, ReactNode } from "react";

import styles from "./Button.module.css";

export type ButtonVariant = "primary" | "secondary" | "ghost";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  /** `large` is the 50–52 px mobile size. */
  size?: "default" | "large";
  icon?: ReactNode;
}

/** Class names for links that look like buttons (navigation stays a real `<a>`). */
export function buttonClass(
  variant: ButtonVariant = "secondary",
  size: "default" | "large" = "default",
): string {
  return [styles.button, styles[variant], size === "large" && styles.large]
    .filter(Boolean)
    .join(" ");
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "secondary", size = "default", icon, className, children, type = "button", ...rest },
  ref,
) {
  const classes = [buttonClass(variant, size), className].filter(Boolean).join(" ");
  return (
    <button ref={ref} type={type} className={classes} {...rest}>
      {icon}
      {children}
    </button>
  );
});

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  /** Accessible name; also shown as tooltip. */
  label: string;
  icon: ReactNode;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, icon, className, type = "button", ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={[styles.iconButton, className].filter(Boolean).join(" ")}
      {...rest}
    >
      {icon}
    </button>
  );
});
